import React, { useState } from 'react';

const Pricing = () => {
  const [isYearly, setIsYearly] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showSavingsAnimation, setShowSavingsAnimation] = useState(false);
  
  // Using your brand colors
  const colors = {
    primary: "#122721",
    background: "#6cf168",
    light: "#f8f9fa",
  };

  // Add custom styles for animations
  const customStyles = `
    @keyframes float-up {
      0% {
        opacity: 0;
        transform: translateY(0) scale(0.5);
      }
      20% {
        opacity: 1;
        transform: translateY(-20px) scale(1);
      }
      100% {
        opacity: 0;
        transform: translateY(-150px) scale(0.8) rotate(15deg);
      }
    }
    @keyframes glow {
      0%, 100% { box-shadow: 0 0 5px rgba(108, 241, 104, 0.5), 0 0 10px rgba(108, 241, 104, 0.3); }
      50% { box-shadow: 0 0 15px rgba(108, 241, 104, 0.7), 0 0 20px rgba(108, 241, 104, 0.5); }
    }
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-5px); }
      75% { transform: translateX(5px); }
    }
    .animate-float-up {
      animation: float-up 2s ease-out forwards;
    }
    .animate-shake {
      animation: shake 0.5s ease-in-out;
    }
    .glow-effect {
      animation: glow 2s ease-in-out infinite;
    }
  `;

  React.useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  const plans = [
    {
      name: "Basic",
      icon: "💼",
      monthlyPrice: 0,
      yearlyPrice: 0,
      description: "Store all your important business documents in one place",
      features: [
        "Business license & trade documents",
        "Tax registration certificate (TRN)",
        "VAT registration certificate",
        "Basic financial dashboard",
      ],
      cta: "Get Started",
      ctaAction: () => window.location.href = 'https://app.arbaah.ae/authentication/sign-up',
      popular: false,
      savings: null
    },
    {
      name: "Starter",
      icon: "📊",
      monthlyPrice: 109,
      yearlyPrice: 799,
      yearlyMonthly: 66.58,
      description: "For freelancers & solo entrepreneurs with regular invoicing",
      features: [
        "Up to 50 invoices/month",
        "Voice-to-Invoice: 50x/month",
        "OCR-Scans: 50x/month",
        "Automatic VAT calculation (5%)",
        "Corporate Tax threshold monitoring (1M AED alert)",
        "Tax deadline reminders",
        "Monthly & quarterly reports",
        "Export as PDF, Excel, CSV",
        "Manual receipt storage"
      ],
      cta: "Start Free Trial",
      ctaAction: () => window.location.href = 'https://app.arbaah.ae/authentication/sign-up',
      popular: false,
      savings: "Save 509 AED per year (39%)"
    },
    {
      name: "Growth",
      icon: "🚀",
      monthlyPrice: 199,
      yearlyPrice: 1299,
      yearlyMonthly: 108.25,
      description: "Perfect for growing businesses with high document volume",
      features: [
        "Unlimited invoices",
        "Voice-to-Invoice: 500x/month",
        "OCR Scans: 500x/month",
        "Real-time dashboard (revenue, costs, profit)",
        "Customer analytics",
        "Tax export (coming soon)",
        "Legal & regulation alerts (coming soon)"
      ],
      cta: "Start Free Trial",
      ctaAction: () => window.location.href = 'https://app.arbaah.ae/authentication/sign-up',
      popular: true,
      popularLabel: "Most Popular",
      savings: "Save 1.089 AED per year (46%)"
    },
    {
      name: "Enterprise",
      icon: "🧠",
      monthlyPrice: "Custom",
      yearlyPrice: "Custom",
      description: "For large businesses or special setups",
      features: [
        "All features from \"Growth\"",
        "Setup consultation with the Arbaah team",
        "Migration & onboarding support",
        "Platform Integration (Shopify, Woocommerce and more)",
        "Priority support channel"
      ],
      cta: "Contact Sales",
      ctaAction: () => setShowEnterpriseModal(true),
      popular: false,
      savings: null
    }
  ];

  // Enterprise Modal Component with full form functionality
  const EnterpriseModal = () => {
    const [formData, setFormData] = useState({
      contactName: '',
      email: '',
      mobileNumber: '',
      companyName: '',
      companySize: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [formSuccess, setFormSuccess] = useState(false);
    const [formError, setFormError] = useState(null);

  const handleSubmit = async () => {
  // Validate required fields
  if (!formData.contactName || !formData.email || !formData.mobileNumber || !formData.companyName) {
    setFormError('Please fill in all required fields');
    return;
  }

  setIsLoading(true);
  setFormError(null);

  try {
    // HubSpot submission
    const portalId = '146261155';
    const formId = 'd6983bc2-7ed5-4676-889d-cea13185e7dc';
    const hubspotUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

    const payload = {
      fields: [
        {
          name: 'firstname',
          value: formData.contactName
        },
        {
          name: 'email',
          value: formData.email
        },
        {
          name: 'phone',
          value: formData.mobileNumber
        },
        {
          name: 'company',
          value: formData.companyName
        },
        {
          name: 'numberofemployees',
          value: formData.companySize || 'Not specified'
        },
        {
          name: 'submission_source',
          value: 'Enterprise Plan Inquiry - Pricing Page'
        },
        {
          name: 'form_location',
          value: 'Pricing Page'
        },
        {
          name: 'lead_type',
          value: 'Enterprise Lead'
        },
        {
          name: 'submission_notes',
          value: `Enterprise plan inquiry. Company: ${formData.companyName}, Size: ${formData.companySize || 'Not specified'}. Interested in enterprise features including setup consultation, migration support, platform integrations, and priority support.`
        },
        {
          name: 'page_url',
          value: window.location.href
        }
      ],
      context: {
        pageUri: window.location.href,
        pageName: 'Arbaah Enterprise Contact'
      }
    };

    const response = await fetch(hubspotUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      setFormSuccess(true);
      setFormData({
        contactName: '',
        email: '',
        mobileNumber: '',
        companyName: '',
        companySize: ''
      });
      setTimeout(() => {
        setShowEnterpriseModal(false);
        setFormSuccess(false);
      }, 3000);
    } else {
      throw new Error('Failed to submit form');
    }
  } catch (error) {
    console.error('Form submission error:', error);
    setFormError('Something went wrong. Please try again.');
  } finally {
    setIsLoading(false);
  }
};

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowEnterpriseModal(false)}>
        <div className="bg-white rounded-xl p-6 md:p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4 md:mb-6">
            <h3 className="text-xl md:text-2xl font-bold" style={{ color: colors.primary }}>Contact Sales</h3>
            <button 
              onClick={() => setShowEnterpriseModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          {formSuccess ? (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">
              <p className="font-medium text-sm md:text-base">🎉 Thank you! We'll contact you soon.</p>
            </div>
          ) : (
            <>
              <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
                Fill out the form below and our team will reach out to discuss your enterprise needs.
              </p>
              <div className="space-y-3 md:space-y-4">
                <div>
                  <div className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Contact Name *</div>
                  <input 
                    type="text" 
                    placeholder="John Doe" 
                    className="w-full px-3 py-2 md:px-4 md:py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                    value={formData.contactName}
                    onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                  />
                </div>
                <div>
                  <div className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Email Address *</div>
                  <input 
                    type="email" 
                    placeholder="john@company.com" 
                    className="w-full px-3 py-2 md:px-4 md:py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div>
                  <div className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Mobile Number *</div>
                  <input 
                    type="tel" 
                    placeholder="+971 50 123 4567" 
                    className="w-full px-3 py-2 md:px-4 md:py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                    value={formData.mobileNumber}
                    onChange={(e) => setFormData({...formData, mobileNumber: e.target.value})}
                  />
                </div>
                <div>
                  <div className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Company Name *</div>
                  <input 
                    type="text" 
                    placeholder="ABC Trading LLC" 
                    className="w-full px-3 py-2 md:px-4 md:py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                  />
                </div>
                <div>
                  <div className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Company Size</div>
                  <select 
                    className="w-full px-3 py-2 md:px-4 md:py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                    value={formData.companySize}
                    onChange={(e) => setFormData({...formData, companySize: e.target.value})}
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>
                {formError && (
                  <p className="text-red-500 text-xs md:text-sm">{formError}</p>
                )}
                <button 
                  onClick={handleSubmit}
                  className="w-full py-2.5 md:py-3 rounded-lg text-white font-medium hover:opacity-90 transition-all disabled:opacity-50 text-sm md:text-base"
                  style={{ backgroundColor: colors.primary }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Help Modal Component
  const HelpModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      mobileNumber: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [formSuccess, setFormSuccess] = useState(false);
    const [formError, setFormError] = useState(null);

 const handleSubmit = async () => {
  // Validate required fields
  if (!formData.name || !formData.email || !formData.mobileNumber) {
    setFormError('Please fill in all fields');
    return;
  }

  setIsLoading(true);
  setFormError(null);

  try {
    // HubSpot submission
    const portalId = '146261155';
    const formId = 'd6983bc2-7ed5-4676-889d-cea13185e7dc';
    const hubspotUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

    const payload = {
      fields: [
        {
          name: 'firstname',
          value: formData.name
        },
        {
          name: 'email',
          value: formData.email
        },
        {
          name: 'phone',
          value: formData.mobileNumber
        },
        {
          name: 'submission_source',
          value: 'Help Choosing Plan - Pricing Page'
        },
        {
          name: 'form_location',
          value: 'Pricing Page'
        },
        {
          name: 'lead_type',
          value: 'General Inquiry'
        },
        {
          name: 'submission_notes',
          value: `User requested help choosing the right plan. Contact: ${formData.name}, Email: ${formData.email}. May need guidance on feature comparison and pricing options.`
        },
        {
          name: 'page_url',
          value: window.location.href
        }
      ],
      context: {
        pageUri: window.location.href,
        pageName: 'Arbaah Help Request'
      }
    };

    const response = await fetch(hubspotUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      setFormSuccess(true);
      setFormData({
        name: '',
        email: '',
        mobileNumber: ''
      });
      setTimeout(() => {
        setShowHelpModal(false);
        setFormSuccess(false);
      }, 3000);
    } else {
      throw new Error('Failed to submit form');
    }
  } catch (error) {
    console.error('Form submission error:', error);
    setFormError('Something went wrong. Please try again.');
  } finally {
    setIsLoading(false);
  }
};

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowHelpModal(false)}>
        <div className="bg-white rounded-xl p-6 md:p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4 md:mb-6">
            <h3 className="text-xl md:text-2xl font-bold" style={{ color: colors.primary }}>Need Help Choosing?</h3>
            <button 
              onClick={() => setShowHelpModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          {formSuccess ? (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">
              <p className="font-medium text-sm md:text-base">🎉 Thank you! We'll get back to you soon.</p>
            </div>
          ) : (
            <>
              <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
                Leave your details and we'll help you find the perfect plan for your business.
              </p>
              <div className="space-y-3 md:space-y-4">
                <div>
                  <div className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Name *</div>
                  <input 
                    type="text" 
                    placeholder="Your name" 
                    className="w-full px-3 py-2 md:px-4 md:py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div>
                  <div className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Email *</div>
                  <input 
                    type="email" 
                    placeholder="your.email@example.com" 
                    className="w-full px-3 py-2 md:px-4 md:py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div>
                  <div className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Mobile Number *</div>
                  <input 
                    type="tel" 
                    placeholder="+971 50 123 4567" 
                    className="w-full px-3 py-2 md:px-4 md:py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                    value={formData.mobileNumber}
                    onChange={(e) => setFormData({...formData, mobileNumber: e.target.value})}
                  />
                </div>
                {formError && (
                  <p className="text-red-500 text-xs md:text-sm">{formError}</p>
                )}
                <button 
                  onClick={handleSubmit}
                  className="w-full py-2.5 md:py-3 rounded-lg text-white font-medium hover:opacity-90 transition-all disabled:opacity-50 text-sm md:text-base"
                  style={{ backgroundColor: colors.primary }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Get Help'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <section id="pricing" className="py-12 md:py-16 lg:py-20 relative overflow-hidden" style={{
      background: "linear-gradient(135deg, #6cf168 0%, #36893B 100%)"
    }}>
      {/* Background with abstract shape gradient */}
      <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
        <svg viewBox="0 0 1440 420" className="absolute -top-40 left-0 w-full h-auto opacity-30">
          <path fill="#ffffff" fillOpacity="0.4" d="M0,224L48,208C96,192,192,160,288,170.7C384,181,480,235,576,245.3C672,256,768,224,864,186.7C960,149,1056,107,1152,96C1248,85,1344,107,1392,117.3L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
        </svg>
        <svg viewBox="0 0 1440 420" className="absolute bottom-0 left-0 w-full h-auto opacity-30 rotate-180">
          <path fill="#ffffff" fillOpacity="0.4" d="M0,224L48,213.3C96,203,192,181,288,192C384,203,480,245,576,229.3C672,213,768,139,864,117.3C960,96,1056,128,1152,160C1248,192,1344,224,1392,240L1440,256L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
        </svg>
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-bl from-[#36893B] to-transparent opacity-40 rounded-full blur-3xl transform translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-2/3 bg-gradient-to-tr from-[#36893B] to-transparent opacity-30 rounded-full blur-3xl transform -translate-x-1/4"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4" style={{ color: colors.primary }}>
            Pricing Overview
          </h1>
          <p className="text-lg md:text-xl font-semibold mb-2" style={{ color: colors.primary }}>
            Speak it. Scan it. Track it.
          </p>
           <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-3 mt-4 md:mt-6 text-sm md:text-base">
            <span className="inline-flex items-center font-medium bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-md" style={{ color: colors.primary }}>
              <span className="mr-2 text-xl emoji-glow">✅</span> 60-day free trial – no credit card required
            </span>
            <span className="inline-flex items-center font-medium bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-md" style={{ color: colors.primary }}>
              <span className="mr-2 text-xl emoji-glow">💸</span> Save up to 46% with annual billing
            </span>
          </div>
        </div>

       

        {/* Monthly/Yearly Toggle */}
        <div className="flex justify-center mb-8 md:mb-12">
          <div className="relative">
            <div className="bg-white rounded-full p-1 md:p-1.5 shadow-lg flex gap-3 md:gap-5 items-center relative overflow-visible">
              {!isYearly && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-50 to-green-100 opacity-50 rounded-full"></div>
              )}
              <button
                onClick={() => {
                  setIsYearly(false);
                  setShowSavingsAnimation(false);
                }}
                className={`px-4 py-2 md:px-6 md:py-2.5 rounded-full font-medium transition-all duration-300 text-sm md:text-base ${
                  !isYearly 
                    ? 'text-white shadow-md' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                style={{ backgroundColor: !isYearly ? colors.primary : 'transparent' }}
              >
                Monthly
              </button>
              <button
                onClick={() => {
                  setIsYearly(true);
                  setShowSavingsAnimation(true);
                  setTimeout(() => setShowSavingsAnimation(false), 2000);
                }}
                className={`px-4 py-2 md:px-6 md:py-2.5 rounded-full font-medium transition-all duration-300 flex items-center gap-2 relative text-sm md:text-base ${
                  isYearly 
                    ? 'text-white shadow-md' 
                    : 'text-gray-600 hover:text-gray-800 glow-effect'
                }`}
                style={{ backgroundColor: isYearly ? colors.primary : 'transparent' }}
              >
                <span>Yearly</span>
                {!isYearly && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] md:text-xs px-1.5 py-0.5 md:px-2 md:py-0.5 rounded-full font-bold animate-bounce">
                    -46%
                  </span>
                )}
                {isYearly && <span className="animate-pulse">💚</span>}
              </button>
            </div>
          </div>
          
          {/* Floating savings badges animation */}
          {showSavingsAnimation && (
            <div className="fixed inset-0 pointer-events-none z-50">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="absolute animate-float-up"
                  style={{
                    left: `${20 + Math.random() * 60}%`,
                    animationDelay: `${i * 0.1}s`,
                    bottom: '40%'
                  }}
                >
                  <div className="bg-green-500 text-white px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-bold shadow-lg">
                    {i % 2 === 0 ? 'Save 46%!' : '💸'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <div 
              key={index} 
              className={`bg-white rounded-xl md:rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl relative flex flex-col ${
                plan.popular ? 'ring-4 ring-green-400 ring-opacity-50 transform md:scale-105' : ''
              } ${isYearly && plan.savings ? 'animate-pulse-once' : ''}`}
              style={{
                animation: isYearly && plan.savings && showSavingsAnimation ? 'pulse 0.5s ease-in-out' : ''
              }}
            >
              {plan.popular && plan.popularLabel && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-green-500 to-green-600 text-white py-1 px-4 md:py-1.5 md:px-6 rounded-bl-xl font-semibold text-xs md:text-sm shadow-md">
                  {plan.popularLabel}
                </div>
              )}
              
              <div className="p-4 md:p-6 flex flex-col flex-grow">
                {/* Plan Header */}
                <div className="mb-4 md:mb-6">
                  <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                    <span className="text-2xl md:text-3xl">{plan.icon}</span>
                    <h3 className="text-xl md:text-2xl font-bold" style={{ color: colors.primary }}>
                      {plan.name}
                    </h3>
                  </div>
                  
                  {/* Price Display */}
                  <div className="mb-2 md:mb-3">
                    {plan.monthlyPrice === "Custom" ? (
                      <div className="text-2xl md:text-3xl font-bold" style={{ color: colors.primary }}>
                        Custom pricing
                      </div>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1 md:gap-2">
                          <span className="text-2xl md:text-3xl font-bold" style={{ color: colors.primary }}>
                            {isYearly && plan.yearlyMonthly 
                              ? `${Math.round(plan.yearlyMonthly)} AED` 
                              : `${plan.monthlyPrice} AED`}
                          </span>
                          {plan.monthlyPrice > 0 && (
                            <span className="text-gray-500 text-sm md:text-base">/month</span>
                          )}
                        </div>
                        {isYearly && plan.yearlyPrice > 0 && (
                          <div className="text-xs md:text-sm text-gray-600 mt-1">
                            {plan.yearlyPrice} AED billed annually
                          </div>
                        )}
                      </>
                    )}
                    {isYearly && plan.savings && (
                      <div className={`mt-1.5 md:mt-2 text-xs md:text-sm font-semibold text-green-600 flex items-center gap-1`}>
                        <span className="text-base md:text-lg">🔹</span> 
                        <span>{plan.savings}</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-xs md:text-sm">
                    {plan.description}
                  </p>
                </div>

                {/* Features List */}
                <div className="mb-4 md:mb-6 flex-grow">
                  <p className="font-semibold mb-2 md:mb-3 text-sm md:text-base" style={{ color: colors.primary }}>
                    Includes:
                  </p>
                  <ul className="space-y-1.5 md:space-y-2.5">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start text-xs md:text-sm">
                        <svg 
                          className="w-3 h-3 md:w-4 md:h-4 mt-0.5 mr-1.5 md:mr-2 flex-shrink-0" 
                          fill="none" 
                          stroke={colors.primary} 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <button 
                  onClick={plan.ctaAction}
                  className={`w-full py-2.5 md:py-3 px-3 md:px-4 rounded-lg md:rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 mt-auto text-sm md:text-base ${
                    plan.popular 
                      ? 'text-white shadow-lg hover:shadow-xl' 
                      : 'text-white hover:opacity-90'
                  }`}
                  style={{ 
                    backgroundColor: plan.popular ? colors.primary : '#36893B',
                  }}
                >
                  {plan.cta}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-12 md:mt-16">
          <p className="text-base md:text-lg font-medium" style={{ color: colors.primary }}>
            Need help choosing the right plan?
          </p>
          <button 
            onClick={() => setShowHelpModal(true)}
            className="inline-flex items-center mt-3 md:mt-4 text-base md:text-lg font-medium hover:underline"
            style={{ color: colors.primary }}
          >
            Talk to our team
            <svg className="ml-2 w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Enterprise Modal */}
      {showEnterpriseModal && <EnterpriseModal />}
      
      {/* Help Modal */}
      {showHelpModal && <HelpModal />}
    </section>
  );
};

export default Pricing;